import React, { Component, Fragment, useState, useRef, useEffect } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { Query, Mutation } from 'react-apollo';
import { useQuery, useMutation } from 'react-apollo-hooks';
import gql from 'graphql-tag';
import classNames from 'classnames';
import Select from 'react-select';

import QaCard from '../../components/QaCard';
import QaDetails from '../../components/QaDetails';

import Icon, { ChatIcon, QuoteIcon, CloseIcon } from '../../utils/icons';

import useBodyScrollLock from '../../hooks/useBodyScrollLock';

import { QA_LIST_QUERY } from '../../routes/qa';

const EDIT_QA_MUTATION = gql`
  mutation editQa(
    $id: Int!
    $questions: [QaQuestionInput]
    $slangQuestions: [QaQuestionInput]
    $answers: [QaAnswerInput]
    $tags: [Int]
  ) {
    editQa(
      id: $id
      questions: $questions
      answers: $answers
      slangQuestions: $slangQuestions
      tags: $tags
    )
  }
`;

const FilterableQas = ({ loading, qas, onClick }) => {
  const [filter, setFilter] = useState({
    tags: null,
    text: '',
  });

  const [filteredQas, setFilteredQas] = useState([]);

  useEffect(() => {
    filterQas();
  }, [filter.text, filter.tags, loading]);

  const handleTextFilterChange = e => {
    e.persist();

    setFilter(filter => ({
      ...filter,
      text: e.target.value,
    }));
  };

  const handleTagFilterChange = tags => {
    setFilter({
      ...filter,
      tags,
    });
  };

  const filterQas = () => {
    if (!qas) {
      return;
    }

    const filterTags = filter.tags && filter.tags.map(t => t.value);
    const filterText = filter.text;

    let filteredQas = qas;
    // Filter by tags
    if (filter.tags && filter.tags.length > 0) {
      filteredQas = filteredQas.filter(qa => {
        const qaTags = qa.tags.map(t => t.id);

        const hasAllSameTags =
          filterTags.every(filterTag => qaTags.includes(filterTag)) &&
          qaTags.length >= filterTags.length;

        return hasAllSameTags;
      });
    }

    // Filter by text
    filteredQas = filteredQas.filter(qa => {
      if (
        !Array.isArray(qa.questions) ||
        !Array.isArray(qa.slangQuestions) ||
        !Array.isArray(qa.answers)
      ) {
        return false;
      }

      const searchString =
        qa.questions
          .map(q => q.text)
          .join(' ')
          .toLowerCase() +
          qa.slangQuestions
            .map(q => q.text)
            .join(' ')
            .toLowerCase() +
          qa.answers
            .map(q => q.text)
            .join(' ')
            .toLowerCase() || '';

      return searchString.includes(filterText.toLowerCase());
    });

    setFilteredQas(filteredQas);
  };

  return (
    <div className="merge-qa-list">
      <div className="merge-qa-list-filter">
        <input
          className="merge-qa-list-filter-text"
          type="text"
          id="filter"
          value={filter.text}
          placeholder="Etsi QA"
          onChange={handleTextFilterChange}
        />
      </div>
      <div className="merge-qa-list-grid">
        {filteredQas &&
          filteredQas.map((qa, i) => {
            return (
              <QaCard
                key={`${qa.id}-${i}`}
                {...qa}
                onClick={() => onClick(qa.id)}
              />
            );
          })}
      </div>
    </div>
  );
};

const MergeQaModal = ({ question: initialQuestion, closeModal }) => {
  const [question, setQuestion] = useState(initialQuestion);
  const [mergeTarget, setMergeTarget] = useState('questions');
  const [selectedQaId, selectQaId] = useState(null);

  const { loading, error, data } = useQuery(QA_LIST_QUERY);

  const [editQa] = useMutation(EDIT_QA_MUTATION, {
    update: () => {
      closeModal();
    },
  });

  const handleQuestionChange = e => {
    e.persist();

    setQuestion(e.target.value);
  };

  const selectedQa =
    selectedQaId && data.qas
      ? data.qas.find(qa => qa.id === selectedQaId)
      : null;

  const handleSubmit = e => {
    e.preventDefault();

    const { id, tags } = selectedQa;

    const questions =
      mergeTarget === 'questions'
        ? [...selectedQa.questions, { text: question }].map(q => ({
            text: q.text,
          }))
        : selectedQa.questions.map(q => ({ text: q.text }));

    const slangQuestions =
      mergeTarget === 'slangQuestions'
        ? [...selectedQa.slangQuestions, { text: question }].map(q => ({
            text: q.text,
          }))
        : selectedQa.slangQuestions.map(q => ({ text: q.text }));

    const answers = selectedQa.answers.map(a => ({ text: a.text }));

    // Map tags to tagIds
    const tagIds = tags.map(tag => tag.id);

    editQa({
      variables: {
        id,
        questions,
        answers,
        slangQuestions,
        tags: tagIds,
      },
      refetchQueries: () => ['qas'],
      awaitRefetchQueries: true,
    });
  };

  const mergeQuestionsClasses = classNames({
    'merge-qa-action': true,
    'merge-qa-action-disabled': mergeTarget === 'questions',
  });

  const mergeSlangQuestionsClasses = classNames({
    'merge-qa-action': true,
    'merge-qa-action-disabled': mergeTarget === 'slangQuestions',
  });

  return (
    <Fragment>
      <div className="modal-header">
        <div className="modal-header-title">Yhdistä QA:han</div>
        <div className="modal-header-close" onClick={closeModal}>
          Sulje
        </div>
      </div>
      <div className="modal-content">
        <div className="merge-qa">
          <div className="merge-qa-question">
            <div className="merge-qa-question-text">
              <input
                className="merge-qa-question-text-input"
                type="text"
                id="question"
                value={question}
                onChange={handleQuestionChange}
              />
            </div>
          </div>
          {selectedQaId && (
            <div className="merge-qa-actions">
              <div
                className={mergeQuestionsClasses}
                onClick={() => {
                  setMergeTarget('questions');
                }}
              >
                <div className="merge-qa-action-icon">
                  <Icon icon={QuoteIcon} />
                </div>
                Yhdistä kysymyksenä
              </div>
              <div
                className={mergeSlangQuestionsClasses}
                onClick={() => {
                  setMergeTarget('slangQuestions');
                }}
              >
                <div className="merge-qa-action-icon">
                  <Icon icon={ChatIcon} />
                </div>
                Yhdistä slangina
              </div>
              <div
                className="merge-qa-action merge-qa-action-cancel"
                onClick={() => {
                  selectQaId(null);
                }}
              >
                <div className="merge-qa-action-icon">
                  <Icon icon={CloseIcon} />
                </div>
                Peruuta
              </div>
            </div>
          )}
          {selectedQaId && (
            <div className="merge-qa-target">
              <QaDetails
                {...selectedQa}
                questions={
                  mergeTarget === 'questions'
                    ? [...selectedQa.questions, { text: question }]
                    : selectedQa.questions
                }
                slangQuestions={
                  mergeTarget === 'slangQuestions'
                    ? [...selectedQa.slangQuestions, { text: question }]
                    : selectedQa.slangQuestions
                }
              />
            </div>
          )}
          {!selectedQaId && (
            <div className="merge-qa-info">Valitse yhdistettävä QA</div>
          )}
          {!selectedQaId && (
            <FilterableQas
              qas={data && data.qas}
              onClick={selectQaId}
              loading={loading}
            />
          )}
        </div>
      </div>
      {selectedQaId && (
        <div className="modal-footer">
          <button
            className="modal-footer-submit-button"
            type="submit"
            onClick={handleSubmit}
          >
            Tallenna
          </button>
        </div>
      )}
    </Fragment>
  );
};

export default MergeQaModal;
